import { state } from './store';
import { sanitizeHtml } from './utils/sanitize';

const translations: Record<string, Record<string, string>> = {
  en: {
    nav_home: 'Home',
    nav_levels: 'Levels',
    nav_invite: 'Rewards',
    nav_premium: 'Premium',
    nav_profile: 'Profile',
    game_start: 'Launch Game!',
    game_next: 'Next →',
    truth: 'Truth',
    dare: 'Dare',
    guess: 'Guessed',
    add_player: 'Add Player',
    ready: 'I am ready!',
    loading: 'Loading...',

    // Home screen
    home_greeting: 'Hello,',
    home_try_premium: 'Try Premium',
    home_premium_sub: 'All levels + exclusive games unlocked',
    home_game_modes: 'Game Modes',
    home_invite_title: 'Invite Friends & Win!',
    home_invite_sub: 'Next reward at 3 friends 🎁',
    home_premium_active: 'Premium is active',
    home_premium_active_sub: 'All levels unlocked · Enjoy!',
    home_invite_cta: 'Invite on Telegram →',
    home_invited_count: 'friends invited',
    home_invite_more: 'Invite {n} more →',
    home_invite_forever: 'Every friend = +100 coins forever! 🔥',
    home_invite_reward_coins: '+{n} coins 🪙',
    home_invite_reward_premium: '+ {n} Premium 👑',

    // LevelSelect
    levels_title: 'Levels',

    // GameSetup
    setup_mode: 'GAME MODE',
    setup_how_to: 'How to Play',
    setup_rules_label: '📖 QUICK RULES',
    tutorial_got_it: 'Got it, let\'s play!',
    setup_selected: 'Selected Level',
    setup_change: 'Change ›',
    setup_players: 'Players',
    setup_players_sub: '(2–50)',
    setup_add_player: 'Add Player',
    setup_player_placeholder: 'Player name',
    setup_warning: '⚠️ Enter real names for all players before starting!',
    setup_launch: 'Launch Game!',

    // Premium
    premium_title: 'Premium',
    premium_sub: 'Unlock the whole party, no limits',
    premium_f1_title: 'All Games & Levels Unlocked',
    premium_f1_sub: 'Play any level without limits',
    premium_f2_title: 'Exclusive Cards',
    premium_f2_sub: 'Access to special Premium content',
    premium_f3_title: 'Early Access',
    premium_f3_sub: 'Be the first to play new levels & features',
    premium_choose_plan: 'Choose a Plan',
    premium_month1: '1 MONTH',
    premium_month3: '3 MONTHS',
    premium_month12: '1 YEAR',
    premium_per_day: '~{n}⭐/day',
    premium_get_btn: 'Get Premium',
    premium_cancel: 'Cancel anytime · Secure payment',
    premium_invite_cta: 'Or invite 3 friends → get 3 days free ›',
    premium_buy_coins: 'Buy Coins',
    premium_coins_sub: 'Prefer to unlock levels one by one?',
    premium_buy_btn: 'Buy Coins',
    coin_starter: 'Starter',
    coin_hit: 'Hit',
    coin_mega: 'Mega',
    premium_badge_popular: '🔥 POPULAR',
    premium_badge_best_deal: '⭐ BEST DEAL',
    premium_badge_value: '✨ VALUE',

    // Profile
    profile_settings: 'Settings',
    profile_language: 'Language',
    profile_terms: 'Terms of Use',
    profile_privacy: 'Privacy Policy',
    profile_support: 'Contact Support',
    profile_support_sub: 'pandoragames@proton.me',
    profile_no_premium: 'No Premium subscription',
    profile_no_premium_sub: 'Unlock all levels and games',
    profile_get: 'Get →',
    profile_version: 'Party Games v1.0 · Made with ❤️',

    // Invite / Rewards Hub
    invite_title: 'Invite Friends',
    invite_headline: 'Invite & Earn Big',
    invite_sub: 'Every friend = 100 coins 🪙 + special rewards at milestones!',
    invite_friends_count: 'friends invited so far',
    invite_next: '🎁 Next prize at {n} friends',
    invite_btn: '✈️ Invite on Telegram',
    invite_ladder: 'Rewards Ladder 🎁',
    invite_link_label: 'YOUR INVITE LINK',
    invite_copy: 'Copy',
    invite_copied: 'Link copied! 📋',
    invite_milestone_all_done: '🏆 All milestones unlocked!',

    // Rewards Hub
    rewards_hub_title: 'Rewards',
    rewards_earn_more: 'Earn More',
    rewards_ladder: 'Rewards Ladder',
    rewards_achievements_label: 'Achievements',
    rewards_tasks_label: 'Tasks',
    rewards_invite_sub: 'Each friend = 100 coins 🪙',
    rewards_per_friend_coins: '+100 coins for each friend',
    rewards_done_short: 'Done',
    rewards_earned: 'received',
    rewards_available: 'available',
    rewards_claim_short: 'Claim 🎁',
    rewards_task_claimed: 'Reward claimed! +{n} coins 🪙',
    rewards_task_error: 'Failed to claim reward',
    rewards_email_title: 'Add email',
    rewards_email_popup_title: 'Enter your email',
    rewards_email_popup_sub: 'Get +50 coins as a one-time bonus',
    rewards_email_placeholder: 'your@email.com',
    rewards_email_submit: 'Get 50 coins 🪙',
    rewards_email_invalid: 'Enter a valid email address',
    rewards_channel_title: 'Subscribe to channel',
    rewards_channel_claim: 'Claim',
    rewards_pm_title: 'Allow messages',
    rewards_pm_denied: 'Permission not granted',
    rewards_pm_granted_title: 'Permission granted! ✅',
    rewards_pm_granted_sub: 'Close & reopen the app — then tap Claim below',
    rewards_pm_close_btn: '🔄 Close App',
    rewards_pm_ready_title: 'Tap to collect your bonus!',
    rewards_pm_ready_sub: '+30 🪙 ready for you',
    rewards_pm_claim_btn: '🎁 Claim',
    rewards_pm_retry: 'Reopen the app and try again',

    // TextGameEngine
    game_truth_label: 'TRUTH OR DARE',
    game_players_turn: "PLAYER'S TURN",
    game_ready: 'Ready?',
    game_truth: 'Truth',
    game_truth_sub: 'Answer honestly',
    game_dare: 'Dare',
    game_dare_sub: 'Complete the task',
    game_random: '🎲 Random — Let fate decide!',
    game_nhie_label: 'NEVER HAVE I EVER...',
    game_nhie_tap: 'If you HAVE — tap your name 🍺',
    game_mlt_label: 'MOST LIKELY TO...',
    game_mlt_ready: 'Everyone point on 3...',
    game_mlt_sub: 'Point at the person who fits best!',
    game_mlt_count_btn: '👆 Ready? Count 3... 2... 1...',
    game_mlt_point_ready: 'Get ready to point!',
    game_mlt_point_now: 'POINT NOW!',
    profile_premium_forever: 'Forever 🌟',
    profile_premium_until: 'Until',
    game_wyr_sub: 'Everyone picks one — no thinking too long! ⚡',
    game_option_a: 'OPTION A',
    game_option_b: 'OPTION B',
    game_next_q: 'Next Question →',
    game_or: 'OR',
    game_finish: 'Finish Game',
    game_next_card: 'Next Card →',
    game_next_player: 'Next Player →',
    game_turn_label: 'Turn:',
    viral_title: '📸 Story time!',
    viral_subtitle: 'You got a special dare — tell your friends you\'re playing Party Games! Share a story on Telegram.',
    viral_share_btn: '📤 Share to Story',
    viral_skip: 'Skip',
    viral_skip_hint: 'Sometimes you can skip',
    viral_continue: 'Continue ▶',
    viral_shared: '✅ Shared!',
    viral_dare_label: '📸 SHARE A STORY',
    viral_story_text: '🎮 Playing Party Games with friends!\n🔥 Join us — it\'s a blast!\n👇 Tap to start',
    viral_invite_title: 'Send the game to friends!',
    viral_invite_subtitle: 'Your dare: share Party Games with a friend on Telegram!',
    viral_invite_btn: '📤 Send to Friends',
    viral_invite_dare_label: '📤 SHARE WITH FRIENDS',
    viral_invite_shared: '✅ Sent!',

    // TimerGameEngine
    timer_forehead: 'Place phone on your forehead!',
    timer_ready_btn: 'I am ready!',
    timer_your_word: '👆 Tap to reveal (3 sec)',
    timer_next: 'Next Player →',
    timer_finish: 'Finish →',
    timer_describe_action: 'Describe the word — no synonyms or gestures!',
    timer_act_action: 'Act it out — no words or sounds!',
    timer_your_word_label: 'YOUR WORD',

    // EmojiGameEngine
    emoji_instruction: 'Describe this using ONLY emojis 👇',
    emoji_others: 'Others look at the screen and guess!',
    emoji_express: 'EXPRESS THIS WITH EMOJIS:',
    emoji_tap_hint: '👆 Tap to reveal (3 sec)',
    emoji_tap_label: 'TAP TO ADD:',
    emoji_clear: '🗑️ Clear',
    emoji_guessed: 'Guessed',
    emoji_game_over: 'Game Over!',
    emoji_finish: 'Finish game',

    // Unlock popup
    unlock_success_title: 'Level Unlocked! 🎉',
    unlock_cost_label: 'Spent',
    unlock_play_btn: 'Play! 🚀',
    unlock_buy_btn: 'Unlock for {n} coins 🪙',

    // Badges
    badge_free: 'PLAY',
    badge_premium: 'PREMIUM',
    badge_locked: 'LOCKED',
    badge_open: 'PLAY',
    badge_invite: 'Invite {n} friends',

    // Session Expired
    session_expired_title: 'Session Expired',
    session_expired_text: 'Your session has expired. Due to Telegram limitations, please close this window and launch the app again.',
    session_expired_btn: 'Close App ❌',

    // Banned Screen
    banned_title: 'Account Suspended',
    banned_text: 'Your account has been suspended.',
    banned_reason_label: 'REASON',
    banned_contact: 'If you think this is a mistake, contact support in Telegram.',
    banned_close_btn: 'Close App',

    // Errors & System
    cards_unavailable: 'Cards are being prepared. Please try another level!',
    error_connection_title: 'Connection Lost',
    error_connection_sub: 'Unable to reach the server. Check your internet connection and try again.',
    error_connection_btn: 'Retry Connection 🔄',
    error_connection_help: 'If the problem persists, close and reopen the app',
    error_session_expired: 'Session expired. Restarting...',
    error_access_denied: 'No access to this level',
    error_generic: 'Something went wrong. Please try again.',
    error_not_enough_coins: 'Not enough coins',
    error_copy: 'Failed to copy',
    error_lang: 'Failed to save language',
    toast_lang_changed: 'Language changed 🇬🇧',
    toast_premium: 'Premium activated! 👑',
    toast_coins: 'Coins added! 🪙',
    cancel_btn: 'Cancel',
    invite_share_text: "Let's play games! 🎮🔥",


    // Milestones
    milestone_title_1: 'First Friend',
    milestone_title_3: 'Good Start',
    milestone_title_10: 'Party Starter',
    milestone_title_25: 'Legend',
    milestone_forever: '♾️ 26+ friends · +100 coins each, forever',
    milestone_reward_1: '🏅 +200 coins',
    milestone_reward_3: '3d Premium 👑',
    milestone_reward_10: '14d Premium 👑',
    milestone_reward_25: '∞ Premium 🌟',

    // Difficulty
    difficulty_title: 'Difficulty',
    difficulty_all: 'All',
    difficulty_easy: 'Easy',
    difficulty_medium: 'Medium',
    difficulty_hard: 'Hard',
    difficulty_hint: 'Adjust card difficulty for your group',
    difficulty_easy_desc: 'Common everyday things',
    difficulty_medium_desc: 'Requires general knowledge',
    difficulty_hard_desc: 'For experts only',

    // Tournament — Common
    trn_title: 'Tournament',
    trn_subtitle: 'Teams Battle 🏆',

    // Tournament — Setup
    trn_setup_teams: 'Teams',
    trn_setup_teams_sub: '(2–4 teams, 1+ player each)',
    trn_setup_team_default: 'Team {n}',
    trn_setup_unassigned: 'Unassigned',
    trn_setup_unassigned_sub: 'Tap a player to add them',
    trn_setup_auto_split: '⚡ Auto-split players',
    trn_setup_add_team: 'Add team',
    trn_setup_remove_team: 'Remove team',
    trn_setup_score_target: 'Win at',
    trn_setup_score_pts: 'pts',
    trn_setup_turn_timer: 'Turn timer',
    trn_setup_timer_off: 'Off',
    trn_setup_timer_30: '30s',
    trn_setup_timer_60: '60s',
    trn_setup_timer_90: '90s',
    trn_setup_minigames: 'Mini-games',
    trn_setup_minigames_sub: 'At least one must be on',
    trn_setup_punishment: 'Punishment for losers',
    trn_setup_punishment_sub: 'Optional — what they have to do at the end',
    trn_setup_punishment_placeholder: 'e.g. Sing a song · Push-ups · Drink a shot',
    trn_setup_start: '🚀 Start Tournament',
    trn_setup_warn_teams: 'Need at least 2 teams with 1+ player each',
    trn_setup_warn_minigame: 'Pick at least one mini-game',
    trn_setup_loading: 'Loading cards...',

    // Tournament — Setup (extended)
    trn_setup_collapsed_label: 'Tournament Settings',
    trn_setup_collapsed_sub: 'Score · Timer · Mini-games · Categories · Difficulty · Punishment',
    trn_setup_expand: 'Show settings',
    trn_setup_collapse: 'Hide settings',
    trn_setup_players_section: 'Players',
    trn_setup_players_section_sub: 'Add players first — then drag them to teams',
    trn_setup_team_emoji_hint: 'Tap to change',
    trn_setup_categories: 'Level categories',
    trn_setup_categories_sub: 'Pull cards from these unlock types',
    trn_setup_cat_free: 'Free',
    trn_setup_cat_coin: 'Coin',
    trn_setup_cat_premium: 'Premium',
    trn_setup_cat_invite: 'Invite',
    trn_setup_difficulty: 'Difficulty',
    trn_setup_difficulty_sub: 'Same as in solo games',
    trn_setup_warn_no_levels: 'No accessible levels — check categories',

    // Tournament — In-game (extended)
    trn_play_get_ready: 'Tap when ready',
    trn_play_ready_btn: '✋ I am ready!',
    trn_play_describing: 'Describe the word — no synonyms or gestures!',
    trn_play_acting: 'Act it out — no words or sounds!',
    trn_play_forehead: 'Phone on forehead!',
    trn_play_player_label: 'Showing:',

    // Tournament — Board
    trn_board_score: 'SCORE',
    trn_board_round: 'Round {n}',
    trn_board_turn_of: 'Now Playing:',
    trn_board_player: 'Showing:',
    trn_board_roll: '🎲 Roll the Dice',
    trn_board_rolling: 'Rolling...',
    trn_board_to_win: 'to win',
    trn_board_exit_title: 'Quit tournament?',
    trn_board_exit_text: 'Your progress will be lost.',
    trn_board_exit_confirm: 'Quit',
    trn_board_exit_cancel: 'Keep playing',
    trn_board_resume: 'Resume',

    // Tournament — In-game
    trn_play_team_turn: 'TEAM TURN',
    trn_play_guessed: '✅ Guessed (+1)',
    trn_play_failed: '❌ Skip',
    trn_play_timer_label: 'Time left',
    trn_play_time_up: '⏰ Time is up!',

    // Tournament — Victory
    trn_victory_title: '🏆 Champion!',
    trn_victory_team_won: '{team} wins!',
    trn_victory_punishment_label: 'PUNISHMENT FOR THE LOSERS',
    trn_victory_no_punishment: 'No punishment set — just enjoy the win!',
    trn_victory_rematch: '🔁 Rematch',
    trn_victory_home: '🏠 Home',
    trn_victory_sudden_death: '⚡ SUDDEN DEATH',
    trn_victory_sudden_death_sub: 'Two teams tied — one extra round to decide it!',

    // Sudden Death modal
    trn_sd_modal_title: '⚡ SUDDEN DEATH! ⚡',
    trn_sd_modal_body: 'Teams {teams} are tied! First team to score +1 point wins!',
    trn_sd_modal_ok: 'Got it!',
    trn_sd_max_rounds: '{team} wins — max sudden death rounds reached!',

    // Board actions
    trn_board_undo: '↩ Undo last round',
    trn_board_random_badge: 'Result was random!',

    // Mini-game labels
    trn_mg_wai: 'Who Am I?',
    trn_mg_describe: 'Describe It',
    trn_mg_act: 'Act It Out',
    trn_mg_emoji: 'Emoji Story',

    // CardsManager
    difficulty_expanded: 'Expanded to {level} cards',
    no_truth_cards: 'No truth cards',
    no_dare_cards: 'No dare cards',
    no_cards: 'No more cards',
    cards_finished: "You've seen all cards! Thanks for playing 🎉",

  },
  ru: {
    nav_home: 'Главная',
    nav_levels: 'Уровни',
    nav_invite: 'Награды',
    nav_premium: 'Премиум',
    nav_profile: 'Профиль',
    game_start: 'Запустить игру!',
    game_next: 'Далее →',
    truth: 'Правда',
    dare: 'Действие',
    guess: 'Угадали',
    add_player: 'Добавить игрока',
    ready: 'Я готов!',
    loading: 'Загрузка...',

    // Home screen
    home_greeting: 'Привет,',
    home_try_premium: 'Попробуй Premium',
    home_premium_sub: 'Все уровни + эксклюзивные игры',
    home_game_modes: 'Режимы игры',
    home_invite_title: 'Пригласи друзей и выиграй!',
    home_invite_sub: 'Следующий приз за 3 друга 🎁',
    home_premium_active: 'Premium активен',
    home_premium_active_sub: 'Все уровни открыты · Наслаждайся!',
    home_invite_cta: 'Пригласить в Telegram →',
    home_invited_count: 'друзей приглашено',
    home_invite_more: 'Пригласи ещё {n} →',
    home_invite_forever: 'Каждый друг = +100 монет навсегда! 🔥',
    home_invite_reward_coins: '+{n} монет 🪙',
    home_invite_reward_premium: '+ {n} Premium 👑',

    // LevelSelect
    levels_title: 'Уровни',

    // GameSetup
    setup_mode: 'РЕЖИМ ИГРЫ',
    setup_how_to: 'Как играть',
    setup_rules_label: '📖 ПРАВИЛА',
    tutorial_got_it: 'Понятно, играем!',
    setup_selected: 'Выбранный уровень',
    setup_change: 'Изменить ›',
    setup_players: 'Игроки',
    setup_players_sub: '(2–50)',
    setup_add_player: 'Добавить игрока',
    setup_player_placeholder: 'Имя игрока',
    setup_warning: '⚠️ Введи имена всех игроков перед стартом!',
    setup_launch: 'Запустить игру!',

    // Premium
    premium_title: 'Premium',
    premium_sub: 'Открой всё без ограничений',
    premium_f1_title: 'Все уровни и игры разблокированы',
    premium_f1_sub: 'Играй в любой уровень без ограничений',
    premium_f2_title: 'Доступ к эксклюзивным карточкам',
    premium_f2_sub: 'Только для подписчиков Premium',
    premium_f3_title: 'Ранний доступ',
    premium_f3_sub: 'К новым возможностям и уровням',
    premium_choose_plan: 'Выбери план',
    premium_month1: '1 МЕСЯЦ',
    premium_month3: '3 МЕСЯЦА',
    premium_month12: '1 ГОД',
    premium_per_day: '~{n}⭐/день',
    premium_get_btn: 'Получить Premium',
    premium_cancel: 'Отмена в любое время · Безопасная оплата',
    premium_invite_cta: 'Или пригласи 3 друзей → 3 дня бесплатно ›',
    premium_buy_coins: 'Купить монеты',
    premium_coins_sub: 'Предпочитаешь открывать уровни по одному?',
    premium_buy_btn: 'Купить монеты',
    coin_starter: 'Стартер',
    coin_hit: 'Хит',
    coin_mega: 'Мега',
    premium_badge_popular: '🔥 ПОПУЛЯРНОЕ',
    premium_badge_best_deal: '⭐ ЛУЧШАЯ ЦЕНА',
    premium_badge_value: '✨ ВЫГОДНО',

    // Profile
    profile_settings: 'Настройки',
    profile_language: 'Язык',
    profile_terms: 'Условия использования',
    profile_privacy: 'Политика конфиденциальности',
    profile_support: 'Написать в поддержку',
    profile_support_sub: 'pandoragames@proton.me',
    profile_no_premium: 'Нет подписки Premium',
    profile_no_premium_sub: 'Открой все уровни и игры',
    profile_get: 'Получить →',
    profile_version: 'Party Games v1.0 · Сделано с ❤️',

    // Invite / Rewards Hub
    invite_title: 'Пригласить друзей',
    invite_headline: 'Приглашай и зарабатывай',
    invite_sub: 'Каждый друг = 100 монет 🪙 + награды за milestone!',
    invite_friends_count: 'друзей приглашено',
    invite_next: '🎁 Следующий приз за {n} друзей',
    invite_btn: '✈️ Пригласить в Telegram',
    invite_ladder: 'Лестница наград 🎁',
    invite_link_label: 'ТВОЯ ССЫЛКА-ПРИГЛАШЕНИЕ',
    invite_copy: 'Копировать',
    invite_copied: 'Ссылка скопирована! 📋',
    invite_milestone_all_done: '🏆 Все milestone пройдены!',

    // Rewards Hub
    rewards_hub_title: 'Награды',
    rewards_earn_more: 'Заработай ещё',
    rewards_ladder: 'Лестница Наград',
    rewards_achievements_label: 'Достижения',
    rewards_tasks_label: 'Задания',
    rewards_invite_sub: 'Каждый друг = 100 монет 🪙',
    rewards_per_friend_coins: '+100 монет за каждого друга',
    rewards_done_short: 'Готово',
    rewards_earned: 'получено',
    rewards_available: 'доступно',
    rewards_claim_short: 'Забрать 🎁',
    rewards_task_claimed: 'Награда получена! +{n} монет 🪙',
    rewards_task_error: 'Ошибка получения награды',
    rewards_email_title: 'Добавь email',
    rewards_email_popup_title: 'Введи свой email',
    rewards_email_popup_sub: 'Получи +50 монет единоразовый бонус',
    rewards_email_placeholder: 'твой@email.com',
    rewards_email_submit: 'Получить 50 монет 🪙',
    rewards_email_invalid: 'Введи корректный email адрес',
    rewards_channel_title: 'Подписка на канал',
    rewards_channel_claim: 'Забрать',
    rewards_pm_title: 'Разрешить сообщения',
    rewards_pm_denied: 'Разрешение не получено',
    rewards_pm_granted_title: 'Разрешение выдано! ✅',
    rewards_pm_granted_sub: 'Закройте и перезапустите приложение — затем нажмите Забрать',
    rewards_pm_close_btn: '🔄 Закрыть приложение',
    rewards_pm_ready_title: 'Забери свой бонус!',
    rewards_pm_ready_sub: '+30 🪙 ждут тебя',
    rewards_pm_claim_btn: '🎁 Забрать',
    rewards_pm_retry: 'Перезапусти приложение и попробуй снова',

    // TextGameEngine
    game_truth_label: 'ПРАВДА ИЛИ ДЕЙСТВИЕ',
    game_players_turn: 'ХОД ИГРОКА',
    game_ready: 'Готов?',
    game_truth: 'Правда',
    game_truth_sub: 'Ответь честно',
    game_dare: 'Действие',
    game_dare_sub: 'Выполни задание',
    game_random: '🎲 Случайно — пусть судьба решит!',
    game_nhie_label: 'Я НИКОГДА НЕ...',
    game_nhie_tap: 'Если ты делал это — нажми своё имя 🍺',
    game_mlt_label: 'СКОРЕЕ ВСЕГО...',
    game_mlt_ready: 'Все указывают на счёт 3...',
    game_mlt_sub: 'Укажи на того, кто подходит лучше!',
    game_mlt_count_btn: '👆 Готовы? Считаем 3... 2... 1...',
    game_mlt_point_ready: 'Приготовьтесь указывать!',
    game_mlt_point_now: 'УКАЗЫВАЙТЕ!',
    profile_premium_forever: 'Навсегда 🌟',
    profile_premium_until: 'До',
    game_wyr_sub: 'Каждый выбирает одно — без долгих раздумий! ⚡',
    game_option_a: 'ВАРИАНТ А',
    game_option_b: 'ВАРИАНТ Б',
    game_next_q: 'Следующий вопрос →',
    game_or: 'ИЛИ',
    game_finish: 'Закончить игру',
    game_next_card: 'Следующая карточка →',
    game_next_player: 'Следующий игрок →',
    game_turn_label: 'Ход:',
    viral_title: '📸 Время для истории!',
    viral_subtitle: 'Тебе выпало особое задание — расскажи друзьям, что ты сейчас играешь в Party Games! Опубликуй историю в Telegram.',
    viral_share_btn: '📤 Поделиться в историю',
    viral_skip: 'Пропустить',
    viral_skip_hint: 'Иногда можно пропустить',
    viral_continue: 'Продолжить ▶',
    viral_shared: '✅ Опубликовано!',
    viral_dare_label: '📸 СДЕЛАЙ ИСТОРИЮ',
    viral_story_text: '🎮 Играю в Party Games с друзьями!\n🔥 Присоединяйся — будет весело!\n👇 Нажми, чтобы начать',
    viral_invite_title: 'Отправь игру друзьям!',
    viral_invite_subtitle: 'Твоё задание: отправь Party Games другу в Telegram!',
    viral_invite_btn: '📤 Отправить друзьям',
    viral_invite_dare_label: '📤 ОТПРАВЬ ДРУЗЬЯМ',
    viral_invite_shared: '✅ Отправлено!',

    // TimerGameEngine
    timer_forehead: 'Прислони телефон ко лбу!',
    timer_ready_btn: 'Я готов!',
    timer_your_word: '👆 Нажми чтобы увидеть (3 сек)',
    timer_next: 'Следующий игрок →',
    timer_finish: 'Завершить →',
    timer_describe_action: 'Опиши слово — без синонимов и жестов!',
    timer_act_action: 'Изобрази жестами — без слов и звуков!',
    timer_your_word_label: 'ТВОЁ СЛОВО',

    // EmojiGameEngine
    emoji_instruction: 'Опиши ТОЛЬКО эмодзи 👇',
    emoji_others: 'Другие смотрят на экран и угадывают!',
    emoji_express: 'ИЗОБРАЗИ ЭМОДЗИ:',
    emoji_tap_hint: '👆 Нажми чтобы увидеть (3 сек)',
    emoji_tap_label: 'НАЖМИ ЧТОБЫ ДОБАВИТЬ:',
    emoji_clear: '🗑️ Очистить',
    emoji_guessed: 'Угадали',
    emoji_game_over: 'Игра окончена!',
    emoji_finish: 'Завершить игру',

    // Unlock popup
    unlock_success_title: 'Уровень открыт! 🎉',
    unlock_cost_label: 'Потрачено',
    unlock_play_btn: 'Играть! 🚀',
    unlock_buy_btn: 'Открыть за {n} монет 🪙',

    // Badges
    badge_free: 'ИГРАТЬ',
    badge_premium: 'PREMIUM',
    badge_locked: 'ЗАКРЫТО',
    badge_open: 'ИГРАТЬ',
    badge_invite: 'Пригласи {n} друзей',

    // Session Expired
    session_expired_title: 'Сессия истекла',
    session_expired_text: 'Ваша сессия истекла. Из-за ограничений Telegram, пожалуйста, закройте это окно и откройте приложение заново.',
    session_expired_btn: 'Закрыть ❌',

    // Banned Screen
    banned_title: 'Аккаунт заблокирован',
    banned_text: 'Ваш аккаунт был заблокирован.',
    banned_reason_label: 'ПРИЧИНА',
    banned_contact: 'Если вы считаете это ошибкой, обратитесь в поддержку в Telegram.',
    banned_close_btn: 'Закрыть',

    // Errors & System
    cards_unavailable: 'Карточки готовятся. Попробуйте другой уровень!',
    error_connection_title: 'Связь потеряна',
    error_connection_sub: 'Не удалось связаться с сервером. Проверь подключение к интернету.',
    error_connection_btn: 'Повторить 🔄',
    error_connection_help: 'Если проблема не решается, перезапусти приложение',
    error_session_expired: 'Сессия истекла. Перезапуск...',
    error_access_denied: 'Нет доступа к этому уровню',
    error_generic: 'Что-то пошло не так. Попробуйте ещё раз.',
    error_not_enough_coins: 'Недостаточно монет',
    error_copy: 'Ошибка копирования',
    error_lang: 'Ошибка сохранения языка',
    toast_lang_changed: 'Язык изменён 🇷🇺',
    toast_premium: 'Premium активирован! 👑',
    toast_coins: 'Монеты добавлены! 🪙',
    cancel_btn: 'Отмена',
    invite_share_text: 'Давай сыграем в игру 🔥',


    // Milestones
    milestone_title_1: 'Первый друг',
    milestone_title_3: 'Хорошее начало',
    milestone_title_10: 'Вечеринщик',
    milestone_title_25: 'Легенда',
    milestone_forever: '♾️ 26+ друзей · +100 монет каждый, навсегда',
    milestone_reward_1: '🏅 +200 монет',
    milestone_reward_3: '3д Premium 👑',
    milestone_reward_10: '14д Premium 👑',
    milestone_reward_25: '∞ Premium 🌟',

    // Difficulty
    difficulty_title: 'Сложность',
    difficulty_all: 'Все',
    difficulty_easy: 'Легко',
    difficulty_medium: 'Средне',
    difficulty_hard: 'Сложно',
    difficulty_hint: 'Настройте сложность карточек для вашей компании',
    difficulty_easy_desc: 'Простые повседневные вещи',
    difficulty_medium_desc: 'Нужна общая эрудиция',
    difficulty_hard_desc: 'Только для знатоков',

    // Tournament — Common
    trn_title: 'Турнир',
    trn_subtitle: 'Битва команд 🏆',

    // Tournament — Setup
    trn_setup_teams: 'Команды',
    trn_setup_teams_sub: '(2–4 команды, в каждой 1+ игрок)',
    trn_setup_team_default: 'Команда {n}',
    trn_setup_unassigned: 'Без команды',
    trn_setup_unassigned_sub: 'Тапни игрока чтобы добавить',
    trn_setup_auto_split: '⚡ Распределить автоматически',
    trn_setup_add_team: 'Добавить команду',
    trn_setup_remove_team: 'Удалить команду',
    trn_setup_score_target: 'Игра до',
    trn_setup_score_pts: 'очков',
    trn_setup_turn_timer: 'Таймер на ход',
    trn_setup_timer_off: 'Выкл',
    trn_setup_timer_30: '30 сек',
    trn_setup_timer_60: '60 сек',
    trn_setup_timer_90: '90 сек',
    trn_setup_minigames: 'Мини-игры',
    trn_setup_minigames_sub: 'Минимум одна включена',
    trn_setup_punishment: 'Наказание проигравшим',
    trn_setup_punishment_sub: 'Не обязательно — что они должны сделать в конце',
    trn_setup_punishment_placeholder: 'Например: Спеть песню · 20 отжиманий · Выпить штрафную',
    trn_setup_start: '🚀 Стартуем!',
    trn_setup_warn_teams: 'Нужно минимум 2 команды по 1+ игроку в каждой',
    trn_setup_warn_minigame: 'Выбери хотя бы одну мини-игру',
    trn_setup_loading: 'Загружаю карточки...',

    // Tournament — Setup (расширенное)
    trn_setup_collapsed_label: 'Настройки турнира',
    trn_setup_collapsed_sub: 'Очки · Таймер · Мини-игры · Категории · Сложность · Наказание',
    trn_setup_expand: 'Показать настройки',
    trn_setup_collapse: 'Скрыть настройки',
    trn_setup_players_section: 'Игроки',
    trn_setup_players_section_sub: 'Сначала добавь игроков — потом распредели по командам',
    trn_setup_team_emoji_hint: 'Нажми чтобы сменить',
    trn_setup_categories: 'Категории уровней',
    trn_setup_categories_sub: 'Какие типы уровней использовать',
    trn_setup_cat_free: 'Бесплатные',
    trn_setup_cat_coin: 'За монеты',
    trn_setup_cat_premium: 'Премиум',
    trn_setup_cat_invite: 'За друзей',
    trn_setup_difficulty: 'Сложность',
    trn_setup_difficulty_sub: 'Как в одиночных играх',
    trn_setup_warn_no_levels: 'Нет доступных уровней — проверь категории',

    // Tournament — In-game (расширенное)
    trn_play_get_ready: 'Нажми когда будешь готов',
    trn_play_ready_btn: '✋ Я готов!',
    trn_play_describing: 'Опиши слово — без синонимов и жестов!',
    trn_play_acting: 'Изобрази жестами — без слов и звуков!',
    trn_play_forehead: 'Прислони телефон ко лбу!',
    trn_play_player_label: 'Показывает:',

    // Tournament — Board
    trn_board_score: 'СЧЁТ',
    trn_board_round: 'Раунд {n}',
    trn_board_turn_of: 'Ходит {team}',
    trn_board_player: 'Показывает:',
    trn_board_roll: '🎲 Бросить кубик',
    trn_board_rolling: 'Бросаем...',
    trn_board_to_win: 'до победы',
    trn_board_exit_title: 'Выйти из турнира?',
    trn_board_exit_text: 'Прогресс будет потерян.',
    trn_board_exit_confirm: 'Выйти',
    trn_board_exit_cancel: 'Остаться',
    trn_board_resume: 'Продолжить',

    // Tournament — In-game
    trn_play_team_turn: 'ХОД КОМАНДЫ',
    trn_play_guessed: '✅ Угадали (+1)',
    trn_play_failed: '❌ Пропуск',
    trn_play_timer_label: 'Осталось',
    trn_play_time_up: '⏰ Время вышло!',

    // Tournament — Victory
    trn_victory_title: '🏆 Чемпион!',
    trn_victory_team_won: '{team} побеждает!',
    trn_victory_punishment_label: 'НАКАЗАНИЕ ПРОИГРАВШИМ',
    trn_victory_no_punishment: 'Без наказания — просто наслаждаемся победой!',
    trn_victory_rematch: '🔁 Реванш',
    trn_victory_home: '🏠 На главную',
    trn_victory_sudden_death: '⚡ ВНЕЗАПНАЯ СМЕРТЬ',
    trn_victory_sudden_death_sub: 'Две команды на одинаковом счёте — один доп. раунд решит всё!',

    // Sudden Death modal
    trn_sd_modal_title: '⚡ ВНЕЗАПНАЯ СМЕРТЬ! ⚡',
    trn_sd_modal_body: 'Команды {teams} на одинаковом счёте! Первая наберёт +1 очко — победит!',
    trn_sd_modal_ok: 'Понятно!',
    trn_sd_max_rounds: '{team} побеждает — лимит раундов внезапной смерти исчерпан!',

    // Board actions
    trn_board_undo: '↩ Отменить последний раунд',
    trn_board_random_badge: 'Результат случайный!',

    // Mini-game labels
    trn_mg_wai: 'Кто Я?',
    trn_mg_describe: 'Опиши Слово',
    trn_mg_act: 'Изобрази',
    trn_mg_emoji: 'Эмодзи-история',

    // CardsManager
    difficulty_expanded: 'Расширили выбор до уровня {level}',
    no_truth_cards: 'Нет карточек правды',
    no_dare_cards: 'Нет карточек действия',
    no_cards: 'Карточки кончились',
    cards_finished: 'Вы прошли все карточки! Спасибо за игру 🎉',
  }
};

export function ensureI18nLang(lang?: string): void {
  if (lang) {
    if (!state.user) state.user = {} as any;
    state.user!.lang = ['en', 'ru'].includes(lang) ? lang : 'en';
  } else if (!state.user?.lang) {
    const tgLang = (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.language_code || 'en';
    if (!state.user) state.user = {} as any;
    state.user!.lang = ['en', 'ru'].includes(tgLang) ? tgLang : 'en';
  }
}

export function t(key: string): string {
  const lang = state.user?.lang || 'en';
  const validLang = ['en', 'ru'].includes(lang) ? lang : 'en';
  return translations[validLang]?.[key] || translations['en']?.[key] || key;
}

/**
 * Resolve a multilingual field from KV config.
 * - If `field` is a plain string (legacy) — returns it as-is.
 * - If `field` is a Record — returns field[lang] → field['en'] → first available value → ''.
 */
export function getLocalized(field: Record<string, string> | string | undefined, lang?: string): string {
  if (field === undefined || field === null) return '';
  if (typeof field === 'string') return field;
  const l = lang || state.user?.lang || 'en';
  return field[l] || field['en'] || Object.values(field)[0] || '';
}

export function getLocalizedSafe(field: Record<string, string> | string | undefined, lang?: string): string {
  return sanitizeHtml(getLocalized(field, lang));
}
